import type { OutOfBandTransactionStatus } from "@/modules/finances/boxPaymentAtCounter/entities/outOfBandTransactionType"
import { StripeErrorCode } from "@/modules/finances/types/stripeErrorTypes"
import { convertImageFromDto } from "@modules/dtos/imageDto"
import {
  type LightPaymentErrorDto,
  type TransactionDto,
  type TransactionProductDto,
  TransactionProductType,
  type TransactionUserDto,
} from "@modules/finances/gateway/transactionDto"
import { PaymentMethodType, TransactionStatus } from "@modules/finances/types/financesType"
import { type HUImage } from "@modules/types/imageTypes"

export class TransactionUserEntity {
  id: string | null
  firstname: string | null
  lastname: string | null
  username: string | null
  avatar: HUImage | null
  isAnonymous: boolean

  private constructor(transactionUser: TransactionUserEntity) {
    this.id = transactionUser.id
    this.firstname = transactionUser.firstname
    this.lastname = transactionUser.lastname
    this.username = transactionUser.username
    this.avatar = transactionUser.avatar
    this.isAnonymous = transactionUser.isAnonymous
  }

  static fromDto(transactionUserDto: TransactionUserDto): TransactionUserEntity {
    return new TransactionUserEntity({
      id: transactionUserDto.id,
      firstname: transactionUserDto.firstname,
      lastname: transactionUserDto.lastname,
      username: transactionUserDto.username,
      avatar: transactionUserDto.avatar ? convertImageFromDto(transactionUserDto.avatar) : null,
      isAnonymous: transactionUserDto.isAnonymous,
    })
  }
}

export class TransactionProductEntity {
  id: string
  name: string
  type: TransactionProductType

  private constructor(transactionProduct: TransactionProductEntity) {
    this.id = transactionProduct.id
    this.name = transactionProduct.name
    this.type = transactionProduct.type
  }

  static fromDto(transactionProductDto: TransactionProductDto): TransactionProductEntity {
    return new TransactionProductEntity({
      id: transactionProductDto.id,
      name: transactionProductDto.name,
      type: transactionProductDto.type,
    })
  }
}

export class LightPaymentErrorEntity {
  errorCode: StripeErrorCode
  declineCode: StripeErrorCode | null
  date: Date

  private constructor(lightPaymentError: LightPaymentErrorEntity) {
    this.errorCode = lightPaymentError.errorCode
    this.declineCode = lightPaymentError.declineCode
    this.date = lightPaymentError.date
  }

  static fromDto(lightPaymentErrorDto: LightPaymentErrorDto): LightPaymentErrorEntity {
    return new LightPaymentErrorEntity({
      errorCode: lightPaymentErrorDto.errorCode,
      declineCode: lightPaymentErrorDto.declineCode,
      date: new Date(lightPaymentErrorDto.date),
    })
  }
}

type TransactionEntityState = TransactionEntity
export class TransactionEntity {
  transactionId: string
  date: Date
  paidAt: Date | null
  user: TransactionUserEntity
  amount: number
  fee: number
  netAmount: number
  currency: string
  status: TransactionStatus
  disputed: boolean
  refunded: boolean
  product: TransactionProductEntity
  paymentMethodType: PaymentMethodType
  payoutAt: Date | null
  outOfBandStatus: OutOfBandTransactionStatus | null
  paymentError: LightPaymentErrorEntity | null

  private constructor(transaction: TransactionEntityState) {
    this.transactionId = transaction.transactionId
    this.date = transaction.date
    this.paidAt = transaction.paidAt
    this.user = transaction.user
    this.amount = transaction.amount
    this.fee = transaction.fee
    this.netAmount = transaction.netAmount
    this.currency = transaction.currency
    this.status = transaction.status
    this.disputed = transaction.disputed
    this.refunded = transaction.refunded
    this.product = transaction.product
    this.paymentMethodType = transaction.paymentMethodType
    this.payoutAt = transaction.payoutAt
    this.outOfBandStatus = transaction.outOfBandStatus
    this.paymentError = transaction.paymentError
  }

  static fromDto(transactionDto: TransactionDto): TransactionEntity {
    return new TransactionEntity({
      transactionId: transactionDto.transactionId,
      date: new Date(transactionDto.date),
      paidAt: transactionDto.paidAt ? new Date(transactionDto.paidAt) : null,
      user: TransactionUserEntity.fromDto(transactionDto.user),
      amount: transactionDto.amount,
      fee: transactionDto.fee,
      netAmount: transactionDto.netAmount,
      currency: transactionDto.currency,
      status: transactionDto.status,
      disputed: transactionDto.disputed,
      refunded: transactionDto.refunded,
      product: TransactionProductEntity.fromDto(transactionDto.product),
      paymentMethodType: transactionDto.paymentMethodType,
      payoutAt: transactionDto.payoutAt ? new Date(transactionDto.payoutAt) : null,
      outOfBandStatus: transactionDto.outOfBandStatus,
      paymentError: transactionDto.paymentError ? LightPaymentErrorEntity.fromDto(transactionDto.paymentError) : null,
    })
  }
}
